/**
 * Assignment-centric NP triage — SPA-side types.
 *
 * Mirrors vnext-api/vnext/schemas/backoffice_assignment.py. Wire field
 * names stay snake_case at the boundary; transformers convert to
 * camelCase for in-app use. Reuses NetworkPartnerSummary from
 * network-partner.ts and ResearchPlanItemType from research-plan.ts.
 */

import {
  transformToBackofficeSync,
  transformToBackofficeSyncMarker,
  type ApiBackofficeSync,
  type BackofficeSync,
  type BackofficeSyncMarker,
} from '@/types/backoffice-sync';
import {
  isRegistryPartner,
  type NetworkPartnerSummary,
  type NpReview,
} from '@/types/network-partner';
import {
  formatGbp,
  registryFeeSourceOf,
  toNumber,
  type RegistryFeeSource,
  type RegistryOrderStatus,
} from '@/types/registry-documents';
import type { ResearchPlanItemType } from '@/types/research-plan';

export type BackofficeAssignmentStatus =
  | 'draft'
  | 'shortlisting'
  | 'np_locked'
  | 'sent_to_backoffice';

export interface AssignmentPlanItem {
  /** The membership row's own id — stable for both kinds; use as the React
   * key and the id passed to removeItem (template items have no planItemId). */
  itemId: string;
  /** 'research_plan_item' (automated) | 'template_component' (legacy). */
  kind: string;
  /** Set only for automated (HUMAN plan item) membership; null for template. */
  planItemId: string | null;
  scopeComponentSlug: string;
  scopeComponentName: string | null;
  dimensions: Record<string, unknown>;
  description: string | null;
  subjectId: string | null;
  subjectName: string | null;
  jurisdictionName: string | null;
  status: string | null;
}

export interface AssignmentFile {
  id: string;
  conversationAttachmentId: string;
  filename: string | null;
  contentType: string | null;
  sizeBytes: number | null;
  createdByUserId: string | null;
  createdAt: string | null;
  downloadUrl: string | null;
}

export interface NpShortlistEntry {
  npId: string;
  networkPartner: NetworkPartnerSummary;
  threadId: string | null;
  lastMessageAt: string | null;
  /** Silent document-preview NPRequest in the partner portal, when shared. */
  previewNpRequestId: number | null;
  previewSharedAt: string | null;
}

/** The one origin an assignment can have: kept by the report's registry
 * orders (Armadillo's automatic assignment). Analyst-made ones have none. */
export type AssignmentOrigin = 'registry_documents';

/** Where an automatic assignment stands with Backoffice. Mirrors
 * `BackofficeState` in the API's `registry_documents/partner_backoffice.py`.
 * `syncing` is a fee change of the last couple of minutes whose push has
 * not been confirmed yet; it becomes `behind` once that window passes. */
export type RegistryBackofficeState =
  | 'off'
  | 'waiting_for_project'
  | 'not_billed'
  | 'sent'
  | 'syncing'
  | 'behind';

/** One registry order billed on an assignment, with its share of the fee. */
export interface RegistryAssignmentOrder {
  id: string;
  reference: string;
  companyName: string;
  countryCode: string;
  status: RegistryOrderStatus;
  /** The delivery worker's verdict on an order still awaiting delivery, so
   * the card shows the order as the registry card does; null when none. */
  proposedStatus: RegistryOrderStatus | null;
  /** GBP, with pence. */
  fee: number;
  feeSource: RegistryFeeSource | null;
}

/**
 * What an assignment carries about Armadillo's registry orders: the
 * automatic assignment the orders keep (`origin` set), or a manual one
 * locked to Armadillo that holds orders. Empty for every other assignment,
 * and on an API that predates Armadillo as a network partner.
 */
export interface AssignmentRegistryBilling {
  origin: AssignmentOrigin | null;
  /** The orders billed on this assignment; empty for most. */
  registryOrders: RegistryAssignmentOrder[];
  /** An order on it still waits for its quote, so adds nothing yet. */
  awaitingQuote: boolean;
  /** Null unless the assignment is automatic. */
  backofficeState: RegistryBackofficeState | null;
  /** The automatic assignment's Backoffice request. */
  npRequestId: number | null;
  /** Whole pounds Backoffice was last confirmed to hold (automatic only). */
  backofficeFee: number | null;
  /** The orders' fees summed, GBP; null when it bills none. What the Hub
   * writes `npFee` from (plus any adjustment) on the automatic assignment,
   * and on a manual one locked to Armadillo while it is live. */
  registryFeeTotal: number | null;
  /** The manual assignment that took this automatic one over; it is then
   * cancelled, and its orders are billed there. */
  takenOverById: string | null;
  /** An admin's correction to the fee the orders set, signed GBP with
   * pence: the fee is the orders' total plus this, never below £0. Null
   * when none. */
  feeAdjustment: number | null;
  /** Why the fee was adjusted, as the card shows it. */
  feeAdjustmentNote: string | null;
  feeAdjustmentSetAt: string | null;
  feeAdjustmentSetByUserId: string | null;
}

/** The registry fields of an assignment that has none. */
export const NO_REGISTRY_BILLING: AssignmentRegistryBilling = {
  origin: null,
  registryOrders: [],
  awaitingQuote: false,
  backofficeState: null,
  npRequestId: null,
  backofficeFee: null,
  registryFeeTotal: null,
  takenOverById: null,
  feeAdjustment: null,
  feeAdjustmentNote: null,
  feeAdjustmentSetAt: null,
  feeAdjustmentSetByUserId: null,
};

export interface BackofficeAssignment extends AssignmentRegistryBilling {
  id: string;
  reportId: string;
  teamId: string;
  status: BackofficeAssignmentStatus | string;
  networkPartnerId: string | null;
  networkPartner: NetworkPartnerSummary | null;
  npFee: number | null;
  npCurrency: string | null;
  npDueDate: string | null;
  turnaroundTimeDays: number | null;
  /** The partner deadline as it stands: the fixed date, else the
   * turnaround counted from the report's Start (today until then). */
  effectiveNpDueDate: string | null;
  notes: string | null;
  lockedByUserId: string | null;
  lockedAt: string | null;
  dispatchedAt: string | null;
  completedAt: string | null;
  outcome: string | null;
  /** NP submission notes from the Backoffice callback (not the analyst's note). */
  investigatorNotes: string | null;
  /** QA loop: when the report was last sent back to the partner for rework. */
  reworkSentAt: string | null;
  /** Set when the assignment was cancelled (`outcome` is then 'cancelled'): the
   * assignment, its files and its fee stay as the record of a dropped partner. */
  cancelledAt: string | null;
  cancelledByUserId: string | null;
  cancelReason: string | null;
  /** Persisted: the last push of the fee / deadline to Backoffice failed. */
  backofficeSyncMarker: BackofficeSyncMarker;
  /** Outcome of the push the last edit triggered (the edit's own response). */
  backofficeSync: BackofficeSync | null;
  createdByUserId: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  items: AssignmentPlanItem[];
  attachments: AssignmentFile[];
  shortlist: NpShortlistEntry[];
  review: NpReview | null;
  isReady: boolean;
}

/** Whether the assignment was cancelled (dropped). It stays visible, with its fee. */
export function isAssignmentCancelled(assignment: Pick<BackofficeAssignment, 'outcome'>): boolean {
  return assignment.outcome === 'cancelled';
}

/** Whether the report's registry orders keep this assignment: Armadillo's
 * automatic assignment, read-only everywhere. */
export function isManagedAssignment(assignment: Pick<BackofficeAssignment, 'origin'>): boolean {
  return assignment.origin === 'registry_documents';
}

/** Whether the assignment is Armadillo's: the automatic one, or a manual
 * one locked to the Armadillo partner record. */
export function isRegistryPartnerAssignment(
  assignment: Pick<BackofficeAssignment, 'origin' | 'networkPartner'>
): boolean {
  return isManagedAssignment(assignment) || isRegistryPartner(assignment.networkPartner);
}

/** The statuses of an assignment with a partner on it: locked, or sent to
 * Backoffice. */
const LIVE_ASSIGNMENT_STATUSES: ReadonlySet<string> = new Set(['np_locked', 'sent_to_backoffice']);

/** Whether the assignment is an analyst's own, locked (or sent) to
 * Armadillo and not cancelled. The Hub then writes its fee from the
 * registry orders it bills, as it does the automatic assignment's, and
 * the report's registry-documents card offers new orders. Mirrors
 * `is_live_manual_registry` in the API's
 * `registry_documents/partner_assignment.py`. */
export function isLiveManualRegistryAssignment(
  assignment: Pick<BackofficeAssignment, 'origin' | 'networkPartner' | 'status' | 'outcome'>
): boolean {
  return (
    !isManagedAssignment(assignment) &&
    isRegistryPartner(assignment.networkPartner) &&
    LIVE_ASSIGNMENT_STATUSES.has(assignment.status) &&
    !isAssignmentCancelled(assignment)
  );
}

/** The report's automatic Armadillo assignment while it still bills its
 * orders (not cancelled): what locking Armadillo by hand takes over. */
export function liveManagedAssignment(
  assignments: readonly BackofficeAssignment[]
): BackofficeAssignment | null {
  return assignments.find((a) => isManagedAssignment(a) && !isAssignmentCancelled(a)) ?? null;
}

// ---------------------------------------------------------------------------
// Armadillo's assignments, in words
// ---------------------------------------------------------------------------

/** Where an automatic assignment stands with Backoffice, as its card says. */
export const REGISTRY_BACKOFFICE_STATE_LABELS: Record<RegistryBackofficeState, string> = {
  off: 'Hub only (Backoffice off)',
  not_billed: 'Nothing billed: every order closed before it reached Backoffice',
  waiting_for_project: 'Waits for the Backoffice project',
  sent: 'In Backoffice, assigned',
  syncing: 'Updating Backoffice…',
  behind: 'Backoffice is behind',
};

type RegistryFigures = Pick<
  BackofficeAssignment,
  'npFee' | 'registryFeeTotal' | 'awaitingQuote' | 'registryOrders'
>;

/** An automatic assignment's fee: "£120.00", or "£120.00 + quote pending"
 * while an order on it still waits for its quote. */
export function describeManagedFee(assignment: RegistryFigures): string {
  const fee = formatGbp(assignment.npFee ?? assignment.registryFeeTotal ?? 0);
  return assignment.awaitingQuote ? `${fee} + quote pending` : fee;
}

/** "3 registry orders". */
export function describeRegistryOrderCount(
  assignment: Pick<RegistryFigures, 'registryOrders'>
): string {
  const count = assignment.registryOrders.length;
  return `${count} registry order${count === 1 ? '' : 's'}`;
}

/** What Backoffice holds for an automatic assignment that was taken over:
 * its request stays, at £0, or it never had one. */
export function describeTakenOverBackoffice(
  assignment: Pick<BackofficeAssignment, 'backofficeState' | 'backofficeFee'>
): string | null {
  switch (assignment.backofficeState) {
    case 'sent':
      return 'Backoffice request kept at £0';
    case 'behind':
      // No confirmed figure: a send whose outcome is unknown may have made
      // the request, at the old fee.
      return assignment.backofficeFee === null
        ? 'Backoffice may hold a request from an unconfirmed send; £0 is pending'
        : `Backoffice still holds £${assignment.backofficeFee}; £0 is pending`;
    case 'not_billed':
      return 'Never reached Backoffice';
    case 'off':
    case 'waiting_for_project':
    case 'syncing':
      return REGISTRY_BACKOFFICE_STATE_LABELS[assignment.backofficeState];
    default:
      return null;
  }
}

/** Pence, so two amounts add up without float noise. */
function pence(amount: number): number {
  return Math.round(amount * 100);
}

// ---------------------------------------------------------------------------
// The manual adjustment on the fee the registry orders set
// ---------------------------------------------------------------------------

/** The API refuses an adjustment beyond this, either way. */
export const REGISTRY_FEE_ADJUSTMENT_MAX = 100000;

/** The API refuses a longer reason. */
export const REGISTRY_FEE_ADJUSTMENT_REASON_MAX = 500;

/** What `PATCH /backoffice-assignments/{id}/registry-fee-adjustment` takes:
 * the signed amount in GBP, or null to clear it, and why. */
export interface RegistryFeeAdjustmentRequest {
  amount: number | null;
  reason: string;
}

/** Whether the fee can be adjusted here: an assignment whose fee the
 * registry orders set. The automatic assignment, unless a manual one took
 * it over (its orders are billed there, and the API refuses the
 * adjustment); a cancelled row whose orders all closed or failed still
 * takes one, since Armadillo may charge for a failed trace, and the
 * adjustment then reopens the row as the fee. And a manual assignment
 * locked to Armadillo while it is live: its fee field is read-only, so
 * the adjustment is the lever there too. Any other assignment's fee is
 * edited in its own form. */
export function canAdjustRegistryFee(
  assignment: Pick<
    BackofficeAssignment,
    'origin' | 'takenOverById' | 'networkPartner' | 'status' | 'outcome'
  >
): boolean {
  if (isManagedAssignment(assignment)) {
    return assignment.takenOverById === null;
  }
  return isLiveManualRegistryAssignment(assignment);
}

/** Whether the automatic assignment bills no order: none is linked, or
 * every one closed or failed. Its fee is then the adjustment alone, if
 * any, so adjusting it reads as recording a charge. */
export function billsNoOrders(assignment: Pick<BackofficeAssignment, 'registryOrders'>): boolean {
  return assignment.registryOrders.every((order) => order.feeSource === 'not_billable');
}

/** "+£15.00" or "−£15.00", for an adjustment. */
export function formatSignedGbp(amount: number): string {
  const sign = amount < 0 ? '−' : '+';
  return `${sign}${formatGbp(Math.abs(amount))}`;
}

type AdjustedFigures = Pick<
  BackofficeAssignment,
  'npFee' | 'registryFeeTotal' | 'feeAdjustment' | 'feeAdjustmentNote'
>;

/** How the fee the registry orders set comes about: "Orders total £80.00
 * · Adjustment +£15.00 (courier fee) · Fee £95.00", or "Orders total
 * £80.00 · Fee £80.00" without an adjustment. The API never lets the fee
 * go below £0, and the line says so when that held. */
export function describeRegistryFeeBreakdown(assignment: AdjustedFigures): string {
  const { feeAdjustment, feeAdjustmentNote } = assignment;
  const total = assignment.registryFeeTotal ?? 0;
  const adjustedPence = pence(total) + pence(feeAdjustment ?? 0);
  const clamped = adjustedPence < 0;
  const fee = assignment.npFee ?? (clamped ? 0 : adjustedPence / 100);
  let line = `Orders total ${formatGbp(total)}`;
  if (feeAdjustment !== null) {
    const note = feeAdjustmentNote ? ` (${feeAdjustmentNote})` : '';
    line += ` · Adjustment ${formatSignedGbp(feeAdjustment)}${note}`;
  }
  return `${line} · Fee ${formatGbp(fee)}${clamped ? ' (not below £0)' : ''}`;
}

/** `describeRegistryFeeBreakdown` where there is an adjustment to explain;
 * null without one. */
export function describeFeeAdjustment(assignment: AdjustedFigures): string | null {
  return assignment.feeAdjustment === null ? null : describeRegistryFeeBreakdown(assignment);
}

/** "A2": an assignment's position among the report's assignment cards;
 * null when it is not listed. */
export function assignmentLabelFor(
  assignments: readonly Pick<BackofficeAssignment, 'id'>[],
  id: string | null
): string | null {
  const index = id === null ? -1 : assignments.findIndex((a) => a.id === id);
  return index === -1 ? null : `A${index + 1}`;
}

export interface ReportAssignments {
  reportId: string;
  assignments: BackofficeAssignment[];
  unassignedHumanItemIds: string[];
  ready: boolean;
}

/** Patch body for updating an assignment's commercials (camelCase in-app;
 *  the service maps to snake_case at the boundary). */
export interface BackofficeAssignmentUpdate {
  npFee?: number | null;
  npCurrency?: string | null;
  npDueDate?: string | null;
  turnaroundTimeDays?: number | null;
  notes?: string | null;
  /** Why the fee or deadline is changing. Required (10+ characters) once the
   * report is completed — its figures may already have been billed. */
  reason?: string | null;
}

// --- API (snake_case) mirrors ---

export interface ApiAssignmentPlanItem {
  item_id: string;
  kind: string;
  plan_item_id: string | null;
  scope_component_slug: string;
  scope_component_name: string | null;
  dimensions: Record<string, unknown>;
  description: string | null;
  subject_id: string | null;
  subject_name: string | null;
  jurisdiction_name: string | null;
  status: string | null;
}

export interface ApiAssignmentFile {
  id: string;
  conversation_attachment_id: string;
  filename: string | null;
  content_type: string | null;
  size_bytes: number | null;
  created_by_user_id: string | null;
  created_at: string | null;
  download_url: string | null;
}

export interface ApiNpShortlistEntry {
  np_id: string;
  network_partner: NetworkPartnerSummary;
  thread_id: string | null;
  last_message_at: string | null;
  preview_np_request_id?: number | null;
  preview_shared_at?: string | null;
}

export interface ApiBackofficeAssignment {
  id: string;
  report_id: string;
  team_id: string;
  status: string;
  network_partner_id: string | null;
  network_partner: NetworkPartnerSummary | null;
  np_fee: number | string | null;
  np_currency: string | null;
  np_due_date: string | null;
  turnaround_time_days: number | null;
  effective_np_due_date?: string | null;
  notes: string | null;
  locked_by_user_id: string | null;
  locked_at: string | null;
  dispatched_at: string | null;
  completed_at: string | null;
  outcome: string | null;
  investigator_notes: string | null;
  rework_sent_at: string | null;
  cancelled_at?: string | null;
  cancelled_by_user_id?: string | null;
  cancel_reason?: string | null;
  backoffice_sync_error?: string | null;
  backoffice_sync_failed_at?: string | null;
  backoffice_sync?: ApiBackofficeSync | null;
  created_by_user_id: string | null;
  created_at: string | null;
  updated_at: string | null;
  items: ApiAssignmentPlanItem[];
  attachments: ApiAssignmentFile[];
  shortlist: ApiNpShortlistEntry[];
  review: NpReview | null;
  is_ready: boolean;
  /** The registry fields: absent on an API that predates Armadillo as a
   * network partner. */
  origin?: string | null;
  registry_orders?: ApiRegistryAssignmentOrder[] | null;
  awaiting_quote?: boolean;
  backoffice_state?: string | null;
  np_request_id?: number | null;
  backoffice_fee?: number | null;
  registry_fee_total?: number | string | null;
  taken_over_by_id?: string | null;
  fee_adjustment?: number | string | null;
  fee_adjustment_note?: string | null;
  fee_adjustment_set_at?: string | null;
  fee_adjustment_set_by_user_id?: string | null;
}

export interface ApiRegistryAssignmentOrder {
  id: string;
  reference: string;
  company_name: string;
  country_code: string;
  status: RegistryOrderStatus;
  proposed_status?: RegistryOrderStatus | null;
  fee: number | string;
  fee_source: string;
}

export interface ApiReportAssignments {
  report_id: string;
  assignments: ApiBackofficeAssignment[];
  unassigned_human_item_ids: string[];
  ready: boolean;
}

// --- Transformers ---

export function transformToAssignmentPlanItem(api: ApiAssignmentPlanItem): AssignmentPlanItem {
  return {
    itemId: api.item_id,
    kind: api.kind,
    planItemId: api.plan_item_id,
    scopeComponentSlug: api.scope_component_slug,
    scopeComponentName: api.scope_component_name,
    dimensions: api.dimensions ?? {},
    description: api.description,
    subjectId: api.subject_id,
    subjectName: api.subject_name,
    jurisdictionName: api.jurisdiction_name,
    status: api.status,
  };
}

export function transformToAssignmentFile(api: ApiAssignmentFile): AssignmentFile {
  return {
    id: api.id,
    conversationAttachmentId: api.conversation_attachment_id,
    filename: api.filename,
    contentType: api.content_type,
    sizeBytes: api.size_bytes,
    createdByUserId: api.created_by_user_id,
    createdAt: api.created_at,
    downloadUrl: api.download_url,
  };
}

export function transformToNpShortlistEntry(api: ApiNpShortlistEntry): NpShortlistEntry {
  return {
    npId: api.np_id,
    networkPartner: api.network_partner,
    threadId: api.thread_id,
    lastMessageAt: api.last_message_at,
    previewNpRequestId: api.preview_np_request_id ?? null,
    previewSharedAt: api.preview_shared_at ?? null,
  };
}

export function transformToBackofficeAssignment(
  api: ApiBackofficeAssignment
): BackofficeAssignment {
  return {
    id: api.id,
    reportId: api.report_id,
    teamId: api.team_id,
    status: api.status,
    networkPartnerId: api.network_partner_id,
    networkPartner: api.network_partner,
    // np_fee comes over the wire as a JSON number or a stringified Decimal.
    npFee: api.np_fee === null || api.np_fee === undefined ? null : Number(api.np_fee),
    npCurrency: api.np_currency,
    npDueDate: api.np_due_date,
    turnaroundTimeDays: api.turnaround_time_days,
    effectiveNpDueDate: api.effective_np_due_date ?? null,
    notes: api.notes,
    lockedByUserId: api.locked_by_user_id,
    lockedAt: api.locked_at,
    dispatchedAt: api.dispatched_at,
    completedAt: api.completed_at ?? null,
    outcome: api.outcome ?? null,
    investigatorNotes: api.investigator_notes ?? null,
    reworkSentAt: api.rework_sent_at ?? null,
    cancelledAt: api.cancelled_at ?? null,
    cancelledByUserId: api.cancelled_by_user_id ?? null,
    cancelReason: api.cancel_reason ?? null,
    backofficeSyncMarker: transformToBackofficeSyncMarker(
      api.backoffice_sync_error,
      api.backoffice_sync_failed_at
    ),
    backofficeSync: transformToBackofficeSync(api.backoffice_sync),
    createdByUserId: api.created_by_user_id,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
    items: (api.items ?? []).map(transformToAssignmentPlanItem),
    attachments: (api.attachments ?? []).map(transformToAssignmentFile),
    shortlist: (api.shortlist ?? []).map(transformToNpShortlistEntry),
    review: api.review ?? null,
    isReady: api.is_ready,
    ...registryBillingOf(api),
  };
}

const BACKOFFICE_STATES: ReadonlySet<string> = new Set([
  'off',
  'waiting_for_project',
  'not_billed',
  'sent',
  'syncing',
  'behind',
]);

/** The state the wire names, or null for none and for a value this build
 * does not know. */
export function registryBackofficeStateOf(value: unknown): RegistryBackofficeState | null {
  return typeof value === 'string' && BACKOFFICE_STATES.has(value)
    ? (value as RegistryBackofficeState)
    : null;
}

export function transformToRegistryAssignmentOrder(
  api: ApiRegistryAssignmentOrder
): RegistryAssignmentOrder {
  return {
    id: api.id,
    reference: api.reference,
    companyName: api.company_name,
    countryCode: api.country_code,
    status: api.status,
    proposedStatus: api.proposed_status ?? null,
    fee: toNumber(api.fee) ?? 0,
    feeSource: registryFeeSourceOf(api.fee_source),
  };
}

/** The registry fields, read as absent when the API predates them. An
 * origin this build does not know reads as none. */
function registryBillingOf(api: ApiBackofficeAssignment): AssignmentRegistryBilling {
  return {
    origin: api.origin === 'registry_documents' ? 'registry_documents' : null,
    registryOrders: (api.registry_orders ?? []).map(transformToRegistryAssignmentOrder),
    awaitingQuote: api.awaiting_quote === true,
    backofficeState: registryBackofficeStateOf(api.backoffice_state),
    npRequestId: api.np_request_id ?? null,
    backofficeFee: api.backoffice_fee ?? null,
    registryFeeTotal: toNumber(api.registry_fee_total),
    takenOverById: api.taken_over_by_id ?? null,
    feeAdjustment: toNumber(api.fee_adjustment),
    feeAdjustmentNote: api.fee_adjustment_note ?? null,
    feeAdjustmentSetAt: api.fee_adjustment_set_at ?? null,
    feeAdjustmentSetByUserId: api.fee_adjustment_set_by_user_id ?? null,
  };
}

/** What `POST /registry-documents/reports/{id}/partner-assignment/sync`
 * reports: how the reconcile went, and where Backoffice now stands. */
export interface ApiRegistryPartnerSync {
  outcome: string;
  assignment_id?: string | null;
  backoffice_state?: string | null;
  backoffice_sync_error?: string | null;
}

export interface RegistryPartnerSync {
  /** `created`, `updated`, `unchanged`, `taken_over`, `manual`, `off`, … */
  outcome: string;
  assignmentId: string | null;
  backofficeState: RegistryBackofficeState | null;
  backofficeSyncError: string | null;
}

export function transformToRegistryPartnerSync(api: ApiRegistryPartnerSync): RegistryPartnerSync {
  return {
    outcome: api.outcome,
    assignmentId: api.assignment_id ?? null,
    backofficeState: registryBackofficeStateOf(api.backoffice_state),
    backofficeSyncError: api.backoffice_sync_error ?? null,
  };
}

export function transformToReportAssignments(api: ApiReportAssignments): ReportAssignments {
  return {
    reportId: api.report_id,
    assignments: (api.assignments ?? []).map(transformToBackofficeAssignment),
    unassignedHumanItemIds: api.unassigned_human_item_ids ?? [],
    ready: api.ready,
  };
}

/** Re-export so callers grouping the scope grid can type plan items. */
export type { ResearchPlanItemType };
